import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  forgotPassword,
  resetForgotPasswordStatus,
} from "./../../API_Service/Login_Slice";

const ForgotPasswordModal = ({ isOpen, onClose }) => {
  const [username, setUsername] = useState("");

  const dispatch = useDispatch();
  const { loading, forgotPasswordStatus, forgotPasswordMessage } = useSelector(
    (s) => s.auth,
  );

  const submitted = forgotPasswordStatus === "success";

  // Reset everything when modal opens
  useEffect(() => {
    if (isOpen) {
      setUsername("");
      dispatch(resetForgotPasswordStatus());
    }
  }, [isOpen, dispatch]);

  // Escape key to close
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") handleClose();
    };
    if (isOpen) document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen]);

  if (!isOpen) return null;

  const handleClose = () => {
    dispatch(resetForgotPasswordStatus());
    onClose();
  };

  const handleReset = () => {
    if (!username.trim()) return;
    dispatch(forgotPassword(username.trim()));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div className="w-full max-w-md mx-4 bg-white rounded-2xl shadow-xl overflow-hidden">
        {/* Accent bar */}
        <div className="h-1.5 w-full bg-primary" />

        <div className="p-8 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-on-surface">
                Forgot Password
              </h2>
              <p className="text-sm text-on-surface-variant">
                Enter your username and we will send you instructions!
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 rounded-full hover:bg-surface-container text-on-surface-variant transition-colors"
            >
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>

          {!submitted ? (
            <>
              <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">
                  Username <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) =>
                    setUsername(e.target.value.replace(/\s/g, ""))
                  }
                  onKeyDown={(e) => e.key === "Enter" && handleReset()}
                  placeholder="Username"
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 font-medium focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                />

                {/* Inline error from API (e.g. "Username not found") */}
                {forgotPasswordStatus === "error" && forgotPasswordMessage && (
                  <p className="mt-2 text-xs text-red-500 font-medium">
                    {forgotPasswordMessage}
                  </p>
                )}
              </div>

              <div className="flex justify-between items-center gap-3 pt-2">
                <button
                  onClick={handleClose}
                  className="text-sm text-primary font-semibold hover:underline"
                >
                  Remembered? Log In
                </button>
                <button
                  onClick={handleReset}
                  disabled={loading || !username.trim()}
                  className="px-8 py-2.5 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-60 flex items-center gap-2"
                >
                  {loading && (
                    <span className="material-symbols-outlined text-[16px] animate-spin">
                      progress_activity
                    </span>
                  )}
                  {loading ? "Sending..." : "Reset"}
                </button>
              </div>
            </>
          ) : (
            <div className="text-center space-y-4 py-4">
              <span className="material-symbols-outlined text-green-500 text-5xl">
                mark_email_read
              </span>
              <p className="text-sm text-on-surface-variant">
                Instructions have been sent to your registered email address.
              </p>
              <button
                onClick={handleClose}
                className="text-sm text-primary font-semibold hover:underline"
              >
                Back to Login
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordModal;
